
/**
 * A recursive rhythm tree, inspired by the rhythm trees (arbres rythmiques)
 * of OpenMusic.
 *
 * Each node carries a `value`: a positive integer weight, interpreted relative
 * to its siblings' weights to determine how much of the parent's duration that
 * node occupies. A node's proportional duration is therefore derived at
 * traversal time, not stored.
 *
 * The tree is a pure rhythm skeleton: it carries no domain semantics (no
 * rest/pitch/articulation information). Domain concerns should be addressed
 * separately, which keeps the tree reusable across use cases (clicks, future melodies, etc.).
 *
 * The root node's `value` is meaningless (it has no siblings); the absolute
 * duration of the whole tree is supplied by the consumer when traversing it.
 */
type RhythmTree = RhythmLeaf | RhythmNode;

interface RhythmLeaf {
  kind: 'leaf';
  /**
   * Weight relative to siblings' weights. Positive integer.
   * Meaningless on a root node.
   */
  weight: number;
}

interface RhythmNode {
  kind: 'node';
  /**
   * Weight relative to siblings' weights. Positive integer.
   * Meaningless on a root node.
   */
  weight: number;
  children: RhythmTree[];
}

type IsLeaf = (currentDepth: number, isTuplet: boolean) => boolean;
type DivideBy = (currentDepth: number, weight: number, isTuplet: boolean) => number;

/**
 * Make a node according to the specified options
 * @param currentDepth
 * @param weight default 1
 * @param isLeaf a function used to determine whether this node should be a leaf or not
 * @param divideBy how many children to create, if isLeaf === false
 * @param isTuplet is this node being created as part of a tuplet?
 */
function createNode(
    currentDepth = 0,
    weight = 1,
    isLeaf: IsLeaf,
    divideBy: DivideBy,
    isTuplet: boolean = false,
): RhythmTree {
  if (isLeaf(currentDepth, isTuplet)) {
    return {kind: 'leaf', weight: weight};
  }

  const subdivision = divideBy(currentDepth, weight, isTuplet);
  const children: RhythmTree[] = [];
  for (let i = 0; i < subdivision; i++) {
    children.push(createNode(currentDepth + 1, 1, isLeaf, divideBy, subdivision > 2));
  }

  return {kind: 'node', weight: weight, children};
}

/**
 * The sum of the `value` weights of a node's children.
 * Used to derive each child's proportional share of the parent's duration.
 *
 * @throws if the node has no children (the result would be 0 and would make
 *         any derived proportional duration NaN).
 */
function totalWeight(node: RhythmNode): number {
  if (node.children.length === 0) {
    throw new Error('RhythmNode must have at least one child');
  }
  return node.children.reduce((sum, child) => sum + child.weight, 0);
}

interface RhythmEvent {
  /** Absolute duration, in the same unit as the rootDuration passed in. */
  duration: number;
}

/**
 * Flattens a RhythmTree to RhythmEvents from RhythmLeaf nodes.
 *
 * Each leaf's duration is derived from its position in the weight tree and the
 * root's absolute duration. The root's own `value` is ignored. The unit of the
 * output matches the unit of `rootDuration` (seconds, beats, bars, etc.) - the
 * tree itself is unit-agnostic.
 *
 * Output is rhythm-only: it carries just `duration`. Domain concerns such as
 * rests, pitch, or articulation are a separate concern.
 *
 * @throws if a RhythmNode with no children is encountered during traversal.
 */
function flatten(node: RhythmTree, duration: number): RhythmEvent[] {
  if (node.kind === 'leaf') {
    return [{duration}];
  }
  const weight = totalWeight(node);
  return node.children.flatMap((child) => flatten(child, duration * (child.weight / weight)));
}

export {
  RhythmTree,
  RhythmLeaf,
  RhythmNode,
  IsLeaf,
  DivideBy,
  createNode,
  RhythmEvent,
  totalWeight,
  flatten,
};
